import { generateName } from '@runkr/config'

/**
 * Fake rows for panels whose backend does not exist yet (PHASE3 rules, D-048): the League and
 * Friends lists until T3.13 / T3.14. They are drawn under a "Sign in to unlock" / "Coming soon"
 * overlay, are built from names only (no session or user ids) and are never sent anywhere.
 */
export type RowStatus = 'online' | 'racing' | 'offline'

export interface PlaceholderRow {
  rank: number
  name: string
  points: number
  status: RowStatus
  /** Avatar colour slot (a runner index). */
  avatar: number
  isMe: boolean
}

interface PlaceholderList {
  /** Seeds the fake names, so a list looks the same on every visit. */
  seed: string
  /** Generated rows besides your own. */
  size: number
  /** Your row's place in the list (1-based). */
  myRank: number
  topPoints: number
}

/** PLACEHOLDER: the League tab until T3.14. */
export const PLACEHOLDER_LEAGUE: PlaceholderList = {
  seed: 'league',
  size: 6,
  myRank: 4,
  topPoints: 1480,
}

/** PLACEHOLDER: the Friends tab until T3.13. */
export const PLACEHOLDER_FRIENDS: PlaceholderList = {
  seed: 'friends',
  size: 6,
  myRank: 3,
  topPoints: 960,
}

const STATUSES: readonly RowStatus[] = ['online', 'racing', 'offline', 'offline']

function hash(text: string): number {
  let h = 2166136261
  for (let i = 0; i < text.length; i++) h = Math.imul(h ^ text.charCodeAt(i), 16777619)
  return h >>> 0
}

/** mulberry32: a small seeded generator, so the rows are the same for the same seed. */
function seeded(seed: number): () => number {
  let a = seed
  return () => {
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/**
 * The rows of `list` for the player called `myName`: `size` generated adjective-animal names
 * (never `myName`) with falling points, and your own row at `myRank`, highlighted.
 */
export function placeholderRows(list: PlaceholderList, myName: string): PlaceholderRow[] {
  const random = seeded(hash(`${list.seed}:${myName}`))
  const names = new Set<string>([myName])
  const out: PlaceholderRow[] = []
  const total = list.size + 1
  let points = list.topPoints
  for (let rank = 1; rank <= total; rank++) {
    const isMe = rank === list.myRank
    let name = myName
    while (!isMe && names.has(name)) name = generateName(random)
    names.add(name)
    out.push({
      rank,
      name,
      points,
      status: isMe ? 'online' : STATUSES[Math.floor(random() * STATUSES.length)]!,
      avatar: Math.floor(random() * 4),
      isMe,
    })
    points = Math.max(0, points - 40 - Math.floor(random() * 160))
  }
  return out
}
